import Swal from 'sweetalert2';
import { SurveysAPI } from '../../API';

// The Test / Copy Test Link handler. It used to sit on DashboardDetails (header
// buttons); the Testing tab's "Run tests" card now calls it once per market with
// that market's survey row. The row must be the survey's own row as
// getOneDetails returns it (testurl / useUniqueLinks not inherited from the
// parent), which is what the backend's testSurvey reads.

export type TestLinkMode = 'run' | 'copy';

// Same gate as the old header buttons: something to test against.
export const hasTestTarget = (survey: any): boolean => !!survey?.useUniqueLinks || !!survey?.testurl;

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

// The test user id inside the link testSurvey returns. Two forms:
//   prescreen on:  <DX host>/#/PreScreenQues/<surveyId>/<testUserId>  (the id after the survey id)
//   prescreen off: the client test URL with the id substituted         (the first UUID in it)
export const testUserIdFromLink = (url: string): string | null => {
    const prescreen = url.match(new RegExp(`/PreScreenQues/[^/?#]+/(${UUID})`, 'i'));
    if (prescreen) return prescreen[1];
    const direct = url.match(new RegExp(UUID, 'i'));
    return direct ? direct[0] : null;
};

const detectClientDevice = () => {
    const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '').toLowerCase();
    if (/ipad|tablet|kindle|silk|playbook/.test(ua)) return 'tablet';
    if (/android/.test(ua) && !/mobile/.test(ua)) return 'tablet';
    if (/iphone|ipod|windows phone|blackberry|opera mini|mobile/.test(ua)) return 'mobile';
    return 'desktop';
};

export async function runSurveyTest(survey: any, mode: TestLinkMode): Promise<void> {
    const copying = mode === 'copy';
    if (!survey?.parentid) {
        Swal.fire({
            icon: 'info',
            title: 'Select a Child Market',
            text: copying
                ? 'Test URLs are configured per country. Please expand the project and select a specific country to copy the link for.'
                : 'Test URLs are configured per country. Please expand the project and select a specific country to test.',
        });
        return;
    }

    const useUniqueLinks = !!survey?.useUniqueLinks;
    const testUrl = survey?.testurl;
    if (!useUniqueLinks && !testUrl) {
        Swal.fire({
            icon: 'warning',
            title: 'No Test URL',
            text: copying
                ? 'No test URL configured for this market. Please set the Survey Test URL in the survey form.'
                : 'Test URL has not been configured for this survey yet. Please set the Survey Test URL in the survey form.',
        });
        return;
    }

    // Device-compatibility client-side pre-check (Test only). The server-side gate
    // in createSurveyPartnerUser is still the source of truth for real
    // panelist hits — this just prevents PMs from wasting a click testing
    // on a device the survey isn't configured for.
    const compat = survey?.deviceCompatibility;
    if (!copying && compat) {
        const allowed = String(compat).split(',').map((d: string) => d.trim().toLowerCase()).filter(Boolean);
        const current = detectClientDevice();
        if (allowed.length > 0 && !allowed.includes(current)) {
            const pretty = (d: string) => ({ mobile: 'Mobile', desktop: 'Desktop/Laptop', tablet: 'Tablet' } as any)[d] || d;
            Swal.fire({
                icon: 'warning',
                title: 'Device Not Supported',
                html: `This survey is only available on <strong>${allowed.map(pretty).join(', ')}</strong>.<br/>You are currently on <strong>${pretty(current)}</strong>.`,
            });
            return;
        }
    }

    // Persist a TestSurveyUsers row server-side so the test session has a real DB-backed ID
    // that can be cross-verified later. The backend generates the UUID; we use that ID for both
    // the alert and the URL so the popup ID matches the row in the DB and the hit at the client.
    // If the call fails we fall back to a local random ID — the test still works, just isn't tracked.
    let testUserId: string | null = null;
    let resolvedUrl: string | null = null;
    try {
        const result = await SurveysAPI.testSurvey({ surveyId: survey.id });
        // Backend returns { testUrl: '...' } — derive the user ID from that URL when possible.
        const backendTestUrl: string | undefined = result?.testUrl;
        if (backendTestUrl) {
            resolvedUrl = backendTestUrl;
            // The id of the test user row the backend just inserted.
            testUserId = testUserIdFromLink(backendTestUrl);
        }
    } catch (err: any) {
        console.warn(`[runSurveyTest:${mode}] Failed to persist test user:`, err);
        if (useUniqueLinks) {
            const msg = err?.response?.data?.message || err?.message || 'Test unique links exhausted.';
            Swal.fire({ icon: 'error', title: 'Test Failed', text: msg });
            return;
        }
    }

    if (!testUserId || !resolvedUrl) {
        if (useUniqueLinks) {
            Swal.fire({ icon: 'error', title: 'Test Failed', text: 'Backend returned an empty test unique link.' });
            return;
        }
        // Fallback: locally-generated ID, locally-substituted URL. Not tracked in DB.
        testUserId = 'TEST_' + Math.random().toString(36).substring(2, 10) + '_' + Date.now();
        // Mirrors backend injectUserId behavior — keep in sync until a second
        // frontend caller justifies a shared helper.
        // Strict: if the test URL has no placeholder, surface the misconfig
        // to the PM via alert rather than silently opening a placeholder-less URL.
        const placeholderRegex = /\[USER_ID\]|\{userid\}|\{user_id\}/gi;
        if (!placeholderRegex.test(testUrl!)) {
            alert(`Test URL has no [USER_ID] / {userid} / {user_id} placeholder. Update the survey configuration before ${copying ? 'copying' : 'testing'}.`);
            return;
        }
        const encoded = encodeURIComponent(testUserId);
        resolvedUrl = testUrl!.replace(/\[USER_ID\]|\{userid\}|\{user_id\}/gi, encoded);
    }

    if (copying) {
        navigator.clipboard.writeText(resolvedUrl!);
        Swal.fire({ icon: 'success', title: 'Copied!', text: `Test URL copied to clipboard with User ID:\n${testUserId}` });
        return;
    }

    // Show the ID to the PM so they can cross-verify with client
    alert('Test session started with user ID:\n' + testUserId + '\n\nUse this ID to confirm the client recorded the same hit.');

    window.open(resolvedUrl!, '_blank');
}
